import { createColumnHelper } from '@tanstack/react-table';
import { UpstreamEvent } from 'client';

import { SortableHeader } from 'DesignSystem/features/table-context/sortable-header';
import { DefaultTableCell } from 'DesignSystem/features/table-context/table-body';
import { DefaultTableHead } from 'DesignSystem/features/table-context/table-header';

import { Announced } from './announced';
import { CustomerStatusCell } from './customer-status';
import { EventTypes } from './event-types';
import { LocationsImpacted } from './locations-impacted';
import { ProductsAffected } from './products-affected';
import { Severity } from './severity';
import { Status } from './status';
import { Title } from './title';
import { Updated } from './updated';

const columnHelper = createColumnHelper<UpstreamEvent>();

export const columns = [
  columnHelper.accessor('status', {
    id: 'status',
    size: 115,
    meta: {
      label: 'Status',
      isNumeric: false,
      sort: {
        param: 'status',
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header}>
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell}>
        <Status row={props.row} />
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('customer_status', {
    id: 'customer_status',
    size: 140,
    meta: {
      label: 'Your Status',
      isNumeric: false,
      sort: {
        param: 'customer_status',
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header}>
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell}>
        <CustomerStatusCell row={props.row} />
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('severity', {
    id: 'severity',
    size: 115,
    meta: {
      label: 'Severity',
      isNumeric: false,
      sort: {
        param: 'severity',
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header} className="items-end justify-end text-right">
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell} className="justify-center">
        <Severity row={props.row} />
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('title', {
    id: 'title',
    size: 400,
    meta: {
      label: 'Event Description',
      isNumeric: false,
      sort: {
        param: 'title',
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header}>
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell}>
        <Title row={props.row} />
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('published_at', {
    id: 'published_at',
    size: 130,
    meta: {
      label: 'Announced',
      isNumeric: false,
      sort: {
        param: 'published_at',
      },
      align: 'left',
    },
    header: (props) => (
      <DefaultTableHead header={props.header} className="text-center">
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell} className="justify-start">
        <Announced row={props.row} />
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('updated_at', {
    id: 'updated_at',
    size: 130,
    meta: {
      label: 'Updated',
      isNumeric: false,
      sort: {
        param: 'updated_at',
      },
      align: 'left',
    },
    header: (props) => (
      <DefaultTableHead header={props.header} className="text-center">
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell} className="justify-start">
        <Updated row={props.row} />
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('types', {
    id: 'types',
    size: 200,
    meta: {
      label: 'Event Type',
      isNumeric: false,
    },
    header: (props) => <DefaultTableHead header={props.header}>Event Type</DefaultTableHead>,
    cell: (props) => (
      <DefaultTableCell cell={props.cell}>
        <EventTypes row={props.row} />
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('products_affected', {
    id: 'products_affected',
    size: 150,
    meta: {
      label: 'Products Affected',
      isNumeric: true,
      sort: {
        param: 'products_affected',
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header} className="text-center">
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell} className="justify-center">
        <ProductsAffected row={props.row} />
      </DefaultTableCell>
    ),
  }),
  columnHelper.accessor('locations_impacted', {
    id: 'locations_impacted',
    size: 150,
    meta: {
      label: 'Locations Impacted',
      isNumeric: true,
      sort: {
        param: 'locations_impacted',
      },
    },
    header: (props) => (
      <DefaultTableHead header={props.header} className="text-center">
        <SortableHeader column={props.column} />
      </DefaultTableHead>
    ),
    cell: (props) => (
      <DefaultTableCell cell={props.cell} className="justify-center">
        <LocationsImpacted row={props.row} />
      </DefaultTableCell>
    ),
  }),
];

export function getEventQueueColumns(eventMitigationProject: boolean) {
  if (eventMitigationProject) return columns;
  return columns.filter((column) => column.id !== 'customer_status');
}
